import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PredefinedPartLabel } from '@/types';
import type { CADMarker, CADModel } from '@/types/cad';
import { getCADModel } from '@/services/cad.service';
import { type CADPosition, formatCADPosition } from '@/utils/cadCoordinates';
import { validatePartLabel } from '@/utils/cadPartLabels';

export type ModelError = 'model-required' | 'model-load-failed';

interface UseCADPartLabelEditorParams {
  open: boolean;
  cadModelId?: string;
  initialLabels: PredefinedPartLabel[];
  onApply: (labels: PredefinedPartLabel[]) => void;
  onClose: () => void;
}

/**
 * State, model loading and mutation handlers for the CAD part-label editor.
 * Keeps the dialog component a thin orchestrator over presentational markup.
 */
export const useCADPartLabelEditor = ({
  open,
  cadModelId,
  initialLabels,
  onApply,
  onClose,
}: UseCADPartLabelEditorParams) => {
  const { t } = useTranslation();
  const [cadModel, setCadModel] = useState<CADModel | null>(null);
  const [isLoadingModel, setIsLoadingModel] = useState(false);
  const [modelError, setModelError] = useState<ModelError | null>(null);
  const [labels, setLabels] = useState<PredefinedPartLabel[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<CADPosition | null>(null);
  const [labelName, setLabelName] = useState('');
  const [category, setCategory] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const resetFormFields = useCallback(() => {
    setSelectedPosition(null);
    setLabelName('');
    setCategory('');
    setNote('');
    setFormError(null);
  }, []);

  useEffect(() => {
    if (!open) return;

    setLabels(initialLabels);
    resetFormFields();
    setModelError(null);
    setIsLoadingModel(false);

    if (!cadModelId) {
      setModelError('model-required');
      return;
    }
    if (cadModel?.id === cadModelId) {
      return;
    }

    let cancelled = false;
    setCadModel(null);
    setIsLoadingModel(true);
    getCADModel(cadModelId)
      .then((model) => {
        if (!cancelled) setCadModel(model);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          console.error('Failed to load CAD model for part label editor:', error);
          setModelError('model-load-failed');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingModel(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    cadModel?.id,
    cadModelId,
    initialLabels,
    open,
    resetFormFields,
  ]);

  const markers = useMemo<CADMarker[]>(() => {
    const savedMarkers = labels.map((label) => ({
      position: label.position,
      label: label.label,
      type: 'info' as const,
    }));
    if (!selectedPosition) return savedMarkers;
    return [
      ...savedMarkers,
      {
        position: selectedPosition,
        label: t('drbfm.cadLink.labelEditor.selectedPoint', '選択位置'),
        type: 'warning' as const,
      },
    ];
  }, [labels, selectedPosition, t]);

  const handleAddLabel = () => {
    const validation = validatePartLabel(
      {
        label: labelName,
        position: selectedPosition,
        category,
        note,
      },
      labels,
    );
    if (!validation.valid) {
      setFormError(
        validation.error === 'duplicate-label'
          ? t(
              'drbfm.cadLink.labelEditor.duplicateLabel',
              '同じ部位名がすでに登録されています',
            )
          : t(
              'drbfm.cadLink.labelEditor.requiredFields',
              'CAD上の位置、部位名、カテゴリを入力してください',
            ),
      );
      return;
    }

    setLabels((current) => [
      ...current,
      validation.partLabel,
    ]);
    resetFormFields();
  };

  const handleDeleteLabel = (index: number) => {
    setLabels((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleModelPointClick = useCallback((position: CADPosition) => {
    setSelectedPosition(position);
    setFormError(null);
  }, []);

  const handleApply = () => {
    if (labels.length === 0 && initialLabels.length === 0) return;
    onApply(labels);
    onClose();
  };

  const formattedPosition = selectedPosition
    ? formatCADPosition(selectedPosition)
    : t('drbfm.cadLink.labelEditor.noPointSelected', '未選択');

  return {
    cadModel,
    isLoadingModel,
    modelError,
    labels,
    selectedPosition,
    labelName,
    setLabelName,
    category,
    setCategory,
    note,
    setNote,
    formError,
    setFormError,
    markers,
    formattedPosition,
    handleAddLabel,
    handleDeleteLabel,
    handleModelPointClick,
    handleApply,
  };
};
